import type { ActorRole, AssumedBy, AuthActor, AuthSession } from '~/types/domain'

const guestActor: AuthActor = {
  id: '',
  role: 'public',
  name: 'Guest',
  email: '',
  organizerIds: [],
  teamIds: []
}

function buildDisplayName(session: AuthSession): string {
  if (session.nickname) return session.nickname
  const parts = [session.given_name, session.family_name].filter(Boolean)
  return parts.length > 0 ? parts.join(' ') : session.email
}

export function sessionToActor(session: AuthSession): AuthActor {
  const role = (session.role as ActorRole) ?? 'member'
  return {
    id: session.user_id,
    role,
    name: buildDisplayName(session),
    email: session.email,
    organizerIds: [],
    teamIds: []
  }
}

export const useAuthStore = defineStore('auth', () => {
  const currentActor = ref<AuthActor>({ ...guestActor })
  // Set while a platform admin is acting as this user (assume session).
  const assumedBy = ref<AssumedBy | null>(null)
  const isAssumed = computed(() => assumedBy.value !== null)

  const role = computed<ActorRole>(() => currentActor.value.role)
  const isAdmin = computed(() => role.value === 'platform_admin')
  const isMember = computed(() => role.value === 'member')
  const isJudge = computed(() => role.value === 'judge')
  const isVenueStaff = computed(() => role.value === 'venue_staff')
  const isPublic = computed(() => role.value === 'public')
  const isLoggedIn = computed(() => currentActor.value.id !== '')

  // Backward-compatible helpers
  const isOrganizer = computed(() => isMember.value)
  const isTeamLeader = computed(() => isMember.value)
  const isTeamMember = computed(() => isMember.value)

  function setActor(actor: AuthActor) {
    currentActor.value = actor
  }

  function setAssumedBy(value: AssumedBy | null) {
    assumedBy.value = value
  }

  function reset() {
    currentActor.value = { ...guestActor }
    assumedBy.value = null
  }

  function linkOrganizer(organizerId: string) {
    if (!currentActor.value.organizerIds.includes(organizerId)) {
      currentActor.value.organizerIds.push(organizerId)
    }
  }

  function linkTeam(teamId: string) {
    if (!currentActor.value.teamIds.includes(teamId)) {
      currentActor.value.teamIds.push(teamId)
    }
  }

  return {
    currentActor,
    assumedBy,
    role,
    isAdmin,
    isMember,
    isOrganizer,
    isTeamLeader,
    isTeamMember,
    isJudge,
    isVenueStaff,
    isPublic,
    isLoggedIn,
    isAssumed,
    setActor,
    setAssumedBy,
    reset,
    linkOrganizer,
    linkTeam
  }
}, { persist: true })

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot))
}
